/**
 * Subscription Status Component
 * Displays current subscription information with visual status indicators
 * Uses PARACLETE dark theme design tokens
 */
import React, { useEffect } from 'react';
import { useBillingStore, useBillingSelectors, useGracePeriodUrgency } from '../../stores/billingStore';
import { formatDistanceToNow } from 'date-fns';

interface SubscriptionStatusProps {
  showDetails?: boolean;
  compact?: boolean;
}

export const SubscriptionStatus: React.FC<SubscriptionStatusProps> = ({
  showDetails = true,
  compact = false,
}) => {
  const { loadSubscription, isLoading, error } = useBillingStore();
  const {
    isActive,
    isInactive,
    isExpired,
    isCanceled,
    isPastDue,
    isTrialing,
    isInGracePeriod,
    hasAccess,
    daysRemaining,
    graceDaysRemaining,
    isRecurring,
    isOneTime,
    currentTier,
    accessExpiresAt,
    gracePeriodEndsAt,
    lastPaymentDate,
    paymentFailedAt,
  } = useBillingSelectors();

  const gracePeriodUrgency = useGracePeriodUrgency();

  // Load subscription status on mount
  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  if (isLoading) {
    return (
      <div className="animate-pulse" role="status" aria-label="Loading subscription status">
        <div className="h-20 bg-dark-card rounded-lg"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4" role="alert">
        <p className="text-red-400 text-sm">Error loading subscription: {error}</p>
      </div>
    );
  }

  // Get status color and icon
  const getStatusDisplay = () => {
    if (isInGracePeriod && gracePeriodUrgency) {
      return {
        color: gracePeriodUrgency === 'critical' ? 'red' : gracePeriodUrgency === 'urgent' ? 'orange' : 'yellow',
        icon: gracePeriodUrgency === 'critical' ? '🚨' : '⚠️',
        label: 'Grace Period',
        description: `${graceDaysRemaining ?? 0} day${graceDaysRemaining === 1 ? '' : 's'} remaining`,
      };
    }

    if (isTrialing) {
      return {
        color: 'blue',
        icon: '◎',
        label: 'Trial',
        description: daysRemaining != null && daysRemaining > 0 ? `${daysRemaining} days remaining` : 'Trial active',
      };
    }

    if (isActive) {
      return {
        color: 'green',
        icon: '✓',
        label: 'Active',
        description: daysRemaining != null && daysRemaining > 0 ? `${daysRemaining} days remaining` : 'Active',
      };
    }

    if (isPastDue) {
      return {
        color: 'orange',
        icon: '⚠️',
        label: 'Past Due',
        description: 'Payment past due',
      };
    }

    if (isExpired) {
      return {
        color: 'red',
        icon: '✗',
        label: 'Expired',
        description: 'Subscription expired',
      };
    }

    if (isCanceled) {
      return {
        color: 'gray',
        icon: '○',
        label: 'Cancelled',
        description: 'Subscription canceled',
      };
    }

    return {
      color: 'gray',
      icon: '○',
      label: 'Inactive',
      description: 'No active subscription',
    };
  };

  const status = getStatusDisplay();

  // Get status-based colors for dark theme
  const getStatusColors = (color: string) => {
    switch (color) {
      case 'green':
        return {
          bg: 'bg-green-900/30',
          border: 'border-green-500/50',
          text: 'text-green-400',
          textMuted: 'text-green-500',
          badge: 'bg-green-500/20 text-green-400',
        };
      case 'yellow':
        return {
          bg: 'bg-yellow-900/30',
          border: 'border-yellow-500/50',
          text: 'text-yellow-400',
          textMuted: 'text-yellow-500',
          badge: 'bg-yellow-500/20 text-yellow-400',
        };
      case 'orange':
        return {
          bg: 'bg-orange-900/30',
          border: 'border-orange-500/50',
          text: 'text-orange-400',
          textMuted: 'text-orange-500',
          badge: 'bg-orange-500/20 text-orange-400',
        };
      case 'red':
        return {
          bg: 'bg-red-900/30',
          border: 'border-red-500/50',
          text: 'text-red-400',
          textMuted: 'text-red-500',
          badge: 'bg-red-500/20 text-red-400',
        };
      default:
        return {
          bg: 'bg-dark-card',
          border: 'border-dark-border',
          text: 'text-dark-text',
          textMuted: 'text-dark-text-muted',
          badge: 'bg-dark-border text-dark-text-muted',
        };
    }
  };

  const colors = getStatusColors(status.color);

  // Compact view for header/navigation
  if (compact) {
    return (
      <div
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${colors.badge}`}
        role="status"
        aria-label={`Subscription status: ${status.label}`}
      >
        <span aria-hidden="true">{status.icon}</span>
        <span className="font-medium">{status.label}</span>
        {currentTier !== 'free' && (
          <span className="text-xs opacity-75">• {currentTier}</span>
        )}
      </div>
    );
  }

  // Full status card
  return (
    <div
      className={`border rounded-lg p-6 ${colors.bg} ${colors.border}`}
      role="region"
      aria-label="Subscription status details"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl" aria-hidden="true">{status.icon}</span>
            <h3 className={`text-xl font-semibold ${colors.text}`}>
              {status.label}
            </h3>
          </div>
          <p className={`text-sm ${colors.textMuted}`}>
            {status.description}
          </p>
        </div>

        {currentTier !== 'free' && (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors.badge}`}>
            {currentTier.toUpperCase()}
          </span>
        )}
      </div>

      {/* Details */}
      {showDetails && (
        <div className="space-y-3 pt-4 border-t border-dark-border/50">
          {/* Subscription Type */}
          {(isRecurring || isOneTime) && (
            <div className="flex justify-between text-sm">
              <span className="text-dark-text-muted">Type:</span>
              <span className="font-medium text-dark-text">
                {isRecurring ? 'Monthly Recurring' : '30-Day Pass'}
              </span>
            </div>
          )}

          {/* Access Expiry */}
          {accessExpiresAt && hasAccess && (
            <div className="flex justify-between text-sm">
              <span className="text-dark-text-muted">Access expires:</span>
              <span className="font-medium text-dark-text">
                {accessExpiresAt.toLocaleDateString()} ({formatDistanceToNow(accessExpiresAt, { addSuffix: true })})
              </span>
            </div>
          )}

          {/* Grace Period End */}
          {isInGracePeriod && gracePeriodEndsAt && (
            <div className="flex justify-between text-sm">
              <span className="text-dark-text-muted">Grace period ends:</span>
              <span className={`font-medium ${
                graceDaysRemaining <= 1 ? 'text-red-400' :
                graceDaysRemaining <= 3 ? 'text-orange-400' :
                'text-yellow-400'
              }`}>
                {gracePeriodEndsAt.toLocaleDateString()} ({formatDistanceToNow(gracePeriodEndsAt, { addSuffix: true })})
              </span>
            </div>
          )}

          {/* Last Payment */}
          {lastPaymentDate && (
            <div className="flex justify-between text-sm">
              <span className="text-dark-text-muted">Last payment:</span>
              <span className="font-medium text-dark-text">
                {lastPaymentDate.toLocaleDateString()}
              </span>
            </div>
          )}

          {/* Payment Failed */}
          {paymentFailedAt && isInGracePeriod && (
            <div className="flex justify-between text-sm">
              <span className="text-dark-text-muted">Payment failed:</span>
              <span className="font-medium text-red-400">
                {paymentFailedAt.toLocaleDateString()}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Grace Period Warning Message */}
      {isInGracePeriod && (
        <div
          className={`mt-4 p-3 rounded-md ${
            graceDaysRemaining <= 1 ? 'bg-red-900/30 border border-red-500/50' :
            graceDaysRemaining <= 3 ? 'bg-orange-900/30 border border-orange-500/50' :
            'bg-yellow-900/30 border border-yellow-500/50'
          }`}
          role="alert"
        >
          <p className={`text-sm font-medium ${
            graceDaysRemaining <= 1 ? 'text-red-400' :
            graceDaysRemaining <= 3 ? 'text-orange-400' :
            'text-yellow-400'
          }`}>
            {graceDaysRemaining <= 1 && '🚨 URGENT: '}
            {graceDaysRemaining <= 3 && graceDaysRemaining > 1 && '⚠️ '}
            Your payment failed. Update your payment method to restore full access.
          </p>
          {graceDaysRemaining <= 1 && (
            <p className="text-xs text-red-500 mt-1">
              Access will be completely blocked when grace period expires.
            </p>
          )}
        </div>
      )}

      {/* Expired Message */}
      {isExpired && (
        <div className="mt-4 p-3 rounded-md bg-red-900/30 border border-red-500/50" role="alert">
          <p className="text-sm font-medium text-red-400">
            Your subscription has expired. Subscribe to regain access.
          </p>
        </div>
      )}

      {/* Inactive Message */}
      {isInactive && (
        <div className="mt-4 p-3 rounded-md bg-accent-blue/20 border border-accent-blue/50">
          <p className="text-sm font-medium text-accent-blue">
            Subscribe to unlock premium features and unlimited access.
          </p>
        </div>
      )}
    </div>
  );
};

export default SubscriptionStatus;
